/* Chat — navigation allow-list + executor.

   The assistant can only *request* navigation (via the `navigate` tool). The
   browser validates that request against this allow-list before acting, so a
   hallucinated or injected page/section can never push the visitor somewhere
   arbitrary. An unknown page resolves to null and is silently ignored.

   ⚠ THREE-WAY SYNC. These maps, `SITE_MAP` in
   supabase/functions/chat/prompt.ts, and the `page` enum in that file's
   NAVIGATE_TOOL must all agree. If they drift, navigation silently no-ops —
   the model emits a page this validator rejects, and nothing happens anywhere.
   There is no error to notice, which is exactly why it is worth stating twice.

   Every path below was checked against app/ (the filesystem), not a design
   doc. */

import { services } from "@/data/services";
import { servicePages, servicePageSlug } from "@/data/servicePages";

/* page id -> path. */
export const PAGE_PATHS: Record<string, string> = {
  home: "/",
  services: "/services",
  work: "/work",
  websites: "/work/websites",
  "digital-marketing": "/work/digital-marketing",
  clients: "/clients",
  about: "/about",
  faq: "/faq",
  contact: "/contact",
  privacy: "/privacy",
  terms: "/terms",
};

/* Anchors that actually exist in the DOM, per page. A section is only honoured
   if it is listed for the requested page AND an element with that id is really
   present when we try to scroll (scrollToSection no-ops otherwise).

   Where each id lives:
     home               components/sections/{ServicesPreview,WhyAviva,Clients}.tsx
     websites           components/pages/WebsitesPage.tsx (work-grid),
                        components/pages/WebsiteInquiryForm.tsx (website-inquiry)
     digital-marketing  components/pages/WorkPage.tsx
     faq                components/pages/FAQPage.tsx (faq-<group key>) */
const PAGE_SECTIONS: Record<string, readonly string[]> = {
  home: ["services", "about", "clients"],
  websites: ["work-grid", "website-inquiry"],
  "digital-marketing": ["work-grid"],
  faq: [
    "faq-general",
    "faq-results",
    "faq-approach",
    "faq-process",
    "faq-services",
    "faq-websites",
  ],
};

/* The services page is not scrolled by element id: it opens a tab. It takes a
   discipline ("web") or a service id ("video-editing") — the same targets
   ServiceLink and the header send it — and those come straight from the data
   layer, so a service added to data/services.ts is navigable with no edit
   here. Services with a landing page (data/servicePages.ts) open that page
   instead, like a detail page. */
const SERVICE_TARGETS = new Set<string>([
  "marketing",
  "creative",
  "web",
  ...services.map((s) => s.id),
]);
const LANDING_SLUGS = new Set(servicePages.map((p) => p.slug));

export interface NavigateRequest {
  page: string;
  section?: string;
}

export interface ResolvedNavigation {
  path: string;
  /** Validated element id to scroll to, or null. */
  section: string | null;
  /** A discipline or service id to open on /services (see openServicesTarget). */
  servicesTarget?: string;
  /** Set when the request resolved to a service landing page. */
  landingSlug?: string;
  /** True when the target is the page we're already on. */
  samePage: boolean;
}

// next.config.ts sets trailingSlash: true, so the live pathname is "/about/"
// while PAGE_PATHS yields "/about". Normalise both before comparing or every
// same-page scroll would be misread as a cross-page push.
const norm = (p: string) => p.replace(/\/+$/, "") || "/";

/**
 * Validate an assistant navigate request against the allow-list and resolve it
 * to a concrete path + section. Returns null if the page isn't recognised.
 */
export function resolveNavigation(
  req: NavigateRequest,
  currentPath: string,
): ResolvedNavigation | null {
  if (req.page === "services" && req.section) {
    // A landing slug, or a service id that has a landing page -> that page
    const slug = LANDING_SLUGS.has(req.section)
      ? req.section
      : servicePageSlug(req.section);
    if (slug) {
      const path = `/services/${slug}`;
      return {
        path,
        section: null,
        landingSlug: slug,
        samePage: norm(currentPath) === path,
      };
    }
    if (SERVICE_TARGETS.has(req.section)) {
      return {
        path: "/services",
        section: null,
        servicesTarget: req.section,
        samePage: norm(currentPath) === "/services",
      };
    }
  }

  const path = PAGE_PATHS[req.page];
  if (path === undefined) return null; // unknown / hallucinated page

  const allowedSections = PAGE_SECTIONS[req.page] ?? [];
  const section =
    req.section && allowedSections.includes(req.section) ? req.section : null;

  return { path, section, samePage: norm(currentPath) === norm(path) };
}

/** Smooth-scroll to an element id, clearing the fixed header.
 *
 * Offsets by the page's own scroll-padding-top (globals.css), which is sized
 * for the fixed header. Returns false when the id isn't in the DOM, so callers
 * can tell a real scroll from a no-op. */
export function scrollToSection(id: string): boolean {
  const el = document.getElementById(id);
  if (!el) return false;

  const headerH =
    parseInt(getComputedStyle(document.documentElement).scrollPaddingTop, 10) ||
    80;

  const top = el.getBoundingClientRect().top + window.scrollY - headerH;
  window.scrollTo({
    top: Math.max(0, top),
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
  });
  return true;
}

/** Open a discipline tab or a service on /services — the event ServicesPage
 * listens for (see components/ui/ServiceLink.tsx). Returns false while the
 * services page isn't the current route yet, so callers can retry. */
export function openServicesTarget(target: string): boolean {
  if (norm(window.location.pathname) !== "/services") return false;
  window.dispatchEvent(new CustomEvent("services:category", { detail: target }));
  return true;
}
